import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  DeleteForever,
  Warning,
  Info,
  CheckCircle,
  Cancel,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { postData } from '../../../utils/backendApis';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../pages/authentication/AuthContext';

const DataDeletionRequest = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { logout } = useAuth(); // Assuming you have logout function in AuthContext

  const dataToDelete = [
    'Personal information (name, email, phone, address)',
    'Driver\'s license and identification documents',
    'Academic records and lesson history',
    'Payment and billing information',
    'Photos and videos',
    'Communication history',
    'App usage data and preferences',
    'Firebase authentication data',
  ];

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      reason: '',
      confirmDelete: false,
      acknowledgeLegal: false,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('Password is required'),
      reason: Yup.string(),
      confirmDelete: Yup.boolean()
        .oneOf([true], 'You must confirm deletion'),
      acknowledgeLegal: Yup.boolean()
        .oneOf([true], 'You must acknowledge legal retention'),
    }),
    onSubmit: (values) => {
      // Open confirmation dialog instead of submitting immediately
      setConfirmDialog(true);
    },
  });

  const handleFinalDelete = async () => {
    setConfirmDialog(false);
    setLoading(true);

    try {
      const response = await postData(
        'api/delete_account',
        {
          email: formik.values.email,
          password: formik.values.password,
          reason: formik.values.reason,
        },
        'Account deleted successfully',
        'Failed to delete account'
      );

      if (response?.success) {
        setSnackbar({
          open: true,
          message: 'Your account has been permanently deleted. Redirecting...',
          severity: 'success',
        });

        // Logout and redirect after 3 seconds
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 3000);
      }
    } catch (error) {
      console.error('Deletion error:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.error || 'Failed to delete account. Please check your credentials.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            <Typography variant="h6" fontWeight={700}>
              Final Confirmation Required
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight={600} gutterBottom>
              This action is PERMANENT and IRREVERSIBLE!
            </Typography>
            <Typography variant="body2">
              Are you absolutely sure you want to delete your account? All your data will be permanently erased.
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Email: <strong>{formik.values.email}</strong>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setConfirmDialog(false)}
            variant="outlined"
            size="large"
          >
            Cancel
          </Button>
          <Button
            onClick={handleFinalDelete}
            variant="contained"
            color="error"
            size="large"
            startIcon={<DeleteForever />}
          >
            Yes, Delete My Account
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '50%',
              bgcolor: 'error.main',
              mb: 2,
            }}
          >
            <DeleteForever sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h4" gutterBottom fontWeight={700} color="error.main">
            Delete Your Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Exercise your right to be forgotten under the Kenya Data Protection Act
          </Typography>
        </Box>

        {/* Warning Alert */}
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            This action is permanent and cannot be undone!
          </Typography>
          <Typography variant="body2">
            Once processed, all your data will be permanently deleted from our systems.
          </Typography>
        </Alert>

        {/* Info Alert */}
        <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            Due to legal and regulatory requirements, some data may be retained for up to 7 years 
            (e.g., financial records for tax purposes, academic records for NTSA compliance). 
            This data will be securely stored and not used for any other purpose.
          </Typography>
        </Alert>

        {/* Data to be Deleted */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            The following data will be deleted:
          </Typography>
          <List>
            {dataToDelete.map((item, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckCircle color="error" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Form */}
        <form onSubmit={formik.handleSubmit}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
            Verify Your Identity
          </Typography>

          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email Address"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            id="reason"
            name="reason"
            label="Reason for Deletion (Optional but helpful)"
            multiline
            rows={4}
            value={formik.values.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.reason && Boolean(formik.errors.reason)}
            helperText={formik.touched.reason && formik.errors.reason}
            sx={{ mb: 3 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                id="confirmDelete"
                name="confirmDelete"
                checked={formik.values.confirmDelete}
                onChange={formik.handleChange}
                color="error"
              />
            }
            label={
              <Typography variant="body2">
                I understand this action is <strong>permanent and irreversible</strong>. 
                I will lose access to all my data and services.
              </Typography>
            }
            sx={{ mb: 2 }}
          />

          {formik.touched.confirmDelete && formik.errors.confirmDelete && (
            <Typography variant="body2" color="error" sx={{ mb: 2, ml: 4 }}>
              {formik.errors.confirmDelete}
            </Typography>
          )}

          <FormControlLabel
            control={
              <Checkbox
                id="acknowledgeLegal"
                name="acknowledgeLegal"
                checked={formik.values.acknowledgeLegal}
                onChange={formik.handleChange}
                color="error"
              />
            }
            label={
              <Typography variant="body2">
                I acknowledge that certain data may be retained for legal/regulatory purposes 
                for up to 7 years as required by Kenyan law.
              </Typography>
            }
            sx={{ mb: 3 }}
          />

          {formik.touched.acknowledgeLegal && formik.errors.acknowledgeLegal && (
            <Typography variant="body2" color="error" sx={{ mb: 2, ml: 4 }}>
              {formik.errors.acknowledgeLegal}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="error"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteForever />}
              disabled={loading}
              sx={{ minWidth: 200 }}
            >
              {loading ? 'Processing...' : 'Delete My Account'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Cancel />}
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
          </Box>
        </form>

        {/* Contact Info */}
        <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Need help or have questions?
          </Typography>
          <Typography variant="body2" color="primary">
            Email: privacy@moxydrivingschool.co.ke<br />
            Phone: +254 728 018 513
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default DataDeletionRequest;